import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Button, Col, DatePicker, Icon, Row, Table, Tag, Tooltip } from 'antd';
import { Link, withRouter } from 'react-router-dom';
import { CSVLink } from 'react-csv';
import moment from 'moment';
import { withAppContext } from 'context/AppContext';
import { buildEInvoiceUri } from 'utils/routes';
import { fetchEInvoiceUsersCSV, useGetPaginatedEInvoiceUsers } from 'utils/apis/eInvoice';
import { constructColumn, constructColumnFilterRadio, constructColumnFilterSearch, handleOnAggregationTableChange } from 'utils/table/table';
import { StyledCard, CompactExpandedCompanies, UsersTableWrap } from './eInvoice.styles';

const QUICK_FILTER_KEYS = ['unlinked', 'cancelScheduled', 'renewingSoon', 'pastDue', 'noCompanies', 'neverInvoiced'];

const CSV_BACKEND_KEYS = [
  'Username',
  'Account Email',
  'Host',
  'Unlinked',
  'Host Email',
  'Enrolled Date',
  'Status',
  'Subscription',
  'Renewal Date',
  'Plans',
  'Cancel Scheduled',
  'Companies',
  'Invoices',
  'Last Invoice'
];

const COMPANIES_TOOLTIP = 'Total e-Invoice companies (projects) under this account, including companies with no invoices this month.';
const SUBSCRIPTION_TOOLTIP =
  'Rollup across companies under this host. Mixed means different subscription states — expand the row for per-company detail.';
const RENEWAL_TOOLTIP = 'Earliest upcoming renew/end datetime across this host’s companies.';

const EMPTY_SUMMARY = {
  totalHosts: 0,
  totalCompanies: 0,
  totalInvoices: 0,
  cancelScheduledHosts: 0,
  cancelScheduledCompanies: 0,
  unlinkedHosts: 0
};

const sanitizeCSVField = text => {
  if (text === null || text === undefined) {
    return '';
  }

  return String(text)
    .replace(/\r?\n/g, ' ')
    .replace(/"/g, '""')
    .trim();
};

const constructCSVName = () => {
  const curDateTime = moment().format('YYYY_MM_DD_HH_mm_ss');
  return `einvoice_users_${curDateTime}.csv`;
};

const TitleWithTooltip = ({ title, tooltip }) => (
  <span>
    {title}{' '}
    <Tooltip title={tooltip}>
      <Icon type="info-circle" style={{ color: '#aaa' }} />
    </Tooltip>
  </span>
);

const withServerFilter = (column, dataIndex, sorter) => ({
  ...column,
  onFilter: () => true,
  sortOrder: sorter.columnKey === dataIndex ? sorter.order : false
});

const renderHostCell = (text, record) => {
  if (record.isUnlinked) {
    return (
      <Tooltip title="Enrollment exists but no host profile is linked">
        <span style={{ display: 'inline-flex', flexWrap: 'wrap', alignItems: 'center', gap: 4 }}>
          <span>—</span>
          <Tag style={{ marginRight: 0 }}>Unlinked</Tag>
        </span>
      </Tooltip>
    );
  }

  return record.hostId ? <Link to={`${buildEInvoiceUri()}?hostId=${record.hostId}`}>{text || '—'}</Link> : text || '—';
};

const ACTIVE_SUBSCRIPTION_STATUSES = ['active', 'trialing', 'complete'];

const formatRenewalDateTime = value => {
  if (!value) {
    return null;
  }
  const asMoment = typeof value === 'number' ? moment.unix(value) : moment(value);
  if (!asMoment.isValid()) {
    return null;
  }
  return asMoment.format('DD/MM/YYYY hh:mm A');
};

const renderCompanySubscriptionStatus = (status, cancelAtEndDate) => {
  if (ACTIVE_SUBSCRIPTION_STATUSES.includes(status) && cancelAtEndDate) {
    return <Tag color="orange">Cancel scheduled</Tag>;
  }
  if (ACTIVE_SUBSCRIPTION_STATUSES.includes(status)) {
    return <Tag color="green">Active</Tag>;
  }
  if (status === 'past_due') {
    return <Tag color="red">Past Due</Tag>;
  }
  if (status === 'canceled' || status === 'expired') {
    return <Tag color="red">Expired</Tag>;
  }
  return status ? <Tag>{status}</Tag> : '—';
};

const renderSubscriptionStatus = (status, record) => {
  if (record.subscriptionSummary === 'mixed') {
    return (
      <Tooltip title="Host has companies with different subscription states. Expand row for per-company detail.">
        <Tag color="blue">Mixed</Tag>
      </Tooltip>
    );
  }
  if ((status === 'active' || status === 'trialing' || status === 'complete') && record.cancelAtEndDate) {
    return <Tag color="orange">Cancelled</Tag>;
  }
  if (status === 'active' || status === 'trialing' || status === 'complete') {
    return <Tag color="green">Active</Tag>;
  }
  if (status === 'past_due') {
    return <Tag color="red">Past Due</Tag>;
  }
  if (status === 'canceled' || status === 'expired') {
    return <Tag color="red">Expired</Tag>;
  }
  if (!status) {
    return '—';
  }
  return <Tag>{status}</Tag>;
};

const renderRenewalDate = (value, record) => {
  const renewalValue = value || record.nextRenewalDate;
  const status = record.subscriptionStatus;
  const isActivePeriod = ACTIVE_SUBSCRIPTION_STATUSES.includes(status);

  if (!renewalValue) {
    return isActivePeriod ? '—' : status ? 'Subscription Expired' : '—';
  }

  const formatted = formatRenewalDateTime(renewalValue);
  if (!formatted) {
    return '—';
  }

  const asMoment = typeof renewalValue === 'number' ? moment.unix(renewalValue) : moment(renewalValue);
  if (!isActivePeriod && asMoment.isBefore(moment())) {
    return 'Subscription Expired';
  }

  const daysLeft = record.nextRenewalDays;
  if (daysLeft != null && daysLeft >= 0 && daysLeft <= 7) {
    return (
      <span style={{ whiteSpace: 'nowrap' }}>
        {formatted} <Tag color="volcano">{daysLeft}d</Tag>
      </span>
    );
  }

  return <span style={{ whiteSpace: 'nowrap' }}>{formatted}</span>;
};

const SUBSCRIPTION_FILTER_OPTIONS = [
  { label: 'Active', value: 'active' },
  { label: 'Cancelled', value: 'cancelled' },
  { label: 'Past Due', value: 'past_due' },
  { label: 'Expired', value: 'expired' }
];

const constructColumns = ({ filters = {}, sorter = {} }) => [
  withServerFilter(
    {
      ...constructColumn('Username', 'username', { hasAggregationSorter: true, sorter, width: 140 }),
      ...constructColumnFilterSearch('username', 'Search Username', filters)
    },
    'username',
    sorter
  ),
  withServerFilter(
    {
      ...constructColumn('Account Email', 'accountEmail', { hasAggregationSorter: true, sorter, width: 200 }),
      ...constructColumnFilterSearch('accountEmail', 'Search Account Email', filters)
    },
    'accountEmail',
    sorter
  ),
  withServerFilter(
    {
      ...constructColumn('Host', 'hostName', { hasAggregationSorter: true, sorter, width: 180 }),
      ...constructColumnFilterSearch('hostName', 'Search Host', filters),
      render: renderHostCell
    },
    'hostName',
    sorter
  ),
  withServerFilter(
    {
      ...constructColumn('Host Email', 'hostEmail', { hasAggregationSorter: true, sorter, width: 190 }),
      ...constructColumnFilterSearch('hostEmail', 'Search Host Email', filters)
    },
    'hostEmail',
    sorter
  ),
  {
    ...constructColumn('Enrolled Date', 'createdAt', { hasAggregationSorter: true, sorter, width: 115 }),
    sortOrder: sorter.columnKey === 'createdAt' ? sorter.order : false,
    render: value => (value ? moment(value).format('DD/MM/YYYY') : '—')
  },
  withServerFilter(
    {
      title: <TitleWithTooltip title="Subscription" tooltip={SUBSCRIPTION_TOOLTIP} />,
      dataIndex: 'subscriptionStatus',
      key: 'subscriptionStatus',
      width: 120,
      ...constructColumnFilterRadio('subscriptionStatus', SUBSCRIPTION_FILTER_OPTIONS),
      render: renderSubscriptionStatus
    },
    'subscriptionStatus',
    sorter
  ),
  {
    title: <TitleWithTooltip title="Next renewal" tooltip={RENEWAL_TOOLTIP} />,
    dataIndex: 'nextRenewalDate',
    key: 'nextRenewalDate',
    width: 190,
    sorter: () => false,
    sortOrder: sorter.columnKey === 'nextRenewalDate' ? sorter.order : false,
    render: renderRenewalDate
  }
];

const QUICK_FILTER_CHIPS = [
  { key: 'cancelScheduled', label: 'Cancel scheduled' },
  { key: 'renewingSoon', label: 'Renewing in 7 days' },
  { key: 'pastDue', label: 'Past due' },
  { key: 'noCompanies', label: 'No companies' },
  { key: 'neverInvoiced', label: 'Never invoiced' },
  { key: 'unlinked', label: 'Unlinked' }
];

const expandedCompanyRowRender = record => {
  const companies = Array.isArray(record.companies) ? record.companies : [];
  if (!companies.length) {
    return <div style={{ padding: '8px 12px 8px 8px', color: 'rgba(0,0,0,0.45)' }}>No companies under this host.</div>;
  }

  const companyCount = record.companyCount != null ? record.companyCount : companies.length;
  const companyTitle = `Company (${companyCount})`;
  const companyHeader =
    record.hostId && companyCount > 0 ? (
      <Tooltip title={COMPANIES_TOOLTIP}>
        <Link to={`${buildEInvoiceUri()}?hostId=${record.hostId}`}>{companyTitle}</Link>
      </Tooltip>
    ) : (
      companyTitle
    );

  return (
    <CompactExpandedCompanies>
      <div className="expanded-companies-header">
        <div className="expanded-companies-name">{companyHeader}</div>
        <div className="expanded-companies-meta">
          <div className="expanded-companies-plan">Plan</div>
          <div className="expanded-companies-sub">Subscription</div>
          <div className="expanded-companies-renewal">Renewal / Ends</div>
        </div>
      </div>
      {companies.map(row => {
        const formatted = formatRenewalDateTime(row.renewalDate);
        let renewalLabel = '—';
        if (formatted) {
          renewalLabel = row.cancelAtEndDate ? `Ends ${formatted}` : `Renews ${formatted}`;
        }

        return (
          <div className="expanded-companies-row" key={String(row.projectId || row.name)}>
            <div className="expanded-companies-name" title={row.name}>
              {row.name}
            </div>
            <div className="expanded-companies-meta">
              <div className="expanded-companies-plan" title={row.plan}>
                {row.plan || '—'}
              </div>
              <div className="expanded-companies-sub">{renderCompanySubscriptionStatus(row.subscriptionStatus, row.cancelAtEndDate)}</div>
              <div className="expanded-companies-renewal" title={renewalLabel}>
                {renewalLabel}
              </div>
            </div>
          </div>
        );
      })}
    </CompactExpandedCompanies>
  );
};

const EInvoiceUsers = props => {
  const { user, history } = props;
  const [query, setQuery] = useState({ currentPage: 1, limit: 10, filter: {}, sorter: {} });
  const [tableQuery, setTableQuery] = useState({ filters: {}, sorter: {} });
  const [enrolledRange, setEnrolledRange] = useState([]);

  const month = moment().month() + 1;
  const year = moment().year();

  const listQuery = useMemo(
    () => ({
      ...query,
      month,
      year
    }),
    [query, month, year]
  );

  const { isLoading, paginatedData: userList, total, latestData } = useGetPaginatedEInvoiceUsers(listQuery, {
    shouldFetch: Boolean(user && user.isAdmin)
  });

  const summaryData = (latestData && latestData.summary) || EMPTY_SUMMARY;
  const activeQuickFilters = QUICK_FILTER_KEYS.filter(key => query.filter && query.filter[key]);
  const csvLinkRef = useRef(null);
  const [csvRows, setCsvRows] = useState([]);
  const [isCsvLoading, setIsCsvLoading] = useState(false);

  useEffect(() => {
    if (user && !user.isAdmin) {
      history.push('/forbidden');
    }
  }, [user, history]);

  if (!user || !user.isAdmin) {
    return null;
  }

  const csvHeaders = CSV_BACKEND_KEYS.map(key => ({ label: key, key }));

  const handleCsvDownload = async () => {
    setIsCsvLoading(true);
    try {
      const data = await fetchEInvoiceUsersCSV({
        filter: query.filter,
        sort: query.sorter,
        month,
        year
      });
      const rows = (Array.isArray(data) ? data : []).map(row => {
        const translated = {};
        CSV_BACKEND_KEYS.forEach(key => {
          translated[key] = sanitizeCSVField(row[key]);
        });
        return translated;
      });
      setCsvRows(rows);
      setTimeout(() => {
        if (csvLinkRef.current && csvLinkRef.current.link) {
          csvLinkRef.current.link.click();
        }
      }, 0);
    } finally {
      setIsCsvLoading(false);
    }
  };

  const setQuickFilter = (key, enabled) => {
    setQuery(prev => {
      const nextFilter = { ...(prev.filter || {}) };

      if (enabled) {
        nextFilter[key] = true;
      } else {
        delete nextFilter[key];
      }

      return {
        ...prev,
        currentPage: 1,
        filter: nextFilter
      };
    });
  };

  const clearQuickFilters = () => {
    setQuery(prev => {
      const nextFilter = { ...(prev.filter || {}) };
      QUICK_FILTER_KEYS.forEach(key => {
        delete nextFilter[key];
      });

      return {
        ...prev,
        currentPage: 1,
        filter: nextFilter
      };
    });
  };

  const handleEnrolledRangeChange = dates => {
    setEnrolledRange(dates || []);
    setQuery(prev => {
      const nextFilter = { ...(prev.filter || {}) };
      delete nextFilter.enrolledFrom;
      delete nextFilter.enrolledTo;

      if (dates && dates[0] && dates[1]) {
        nextFilter.enrolledFrom = dates[0].format('YYYY-MM-DD');
        nextFilter.enrolledTo = dates[1].format('YYYY-MM-DD');
      }

      return {
        ...prev,
        currentPage: 1,
        filter: nextFilter
      };
    });
  };

  const handleTableChange = (pagination, filters, sorter) => {
    setTableQuery({ filters, sorter });
    handleOnAggregationTableChange({ pagination, filters, sorter }, nextQuery => {
      const enrolledFrom = query.filter && query.filter.enrolledFrom;
      const enrolledTo = query.filter && query.filter.enrolledTo;
      const quickFilters = {};

      QUICK_FILTER_KEYS.forEach(key => {
        if (query.filter && query.filter[key]) {
          quickFilters[key] = true;
        }
      });

      setQuery({
        currentPage: nextQuery.currentPage || 1,
        limit: nextQuery.limit || 10,
        sorter: nextQuery.sorter || {},
        filter: {
          ...(nextQuery.filter || {}),
          ...quickFilters,
          ...(enrolledFrom && enrolledTo ? { enrolledFrom, enrolledTo } : {})
        }
      });
    });
  };

  const summaryTileStyle = clickable => ({
    cursor: clickable ? 'pointer' : 'default',
    borderRadius: 4,
    padding: '4px 8px',
    margin: '-4px -8px'
  });

  return (
    <StyledCard
      title="e-Invoice Users"
      extra={
        <>
          <CSVLink
            ref={csvLinkRef}
            filename={constructCSVName()}
            data={csvRows}
            headers={csvHeaders}
            className="sp-csv"
            style={{ display: 'none' }}
          />
          <Button type="primary" icon="download" loading={isCsvLoading} onClick={handleCsvDownload}>
            Download CSV
          </Button>
        </>
      }
    >
      <Row gutter={16} style={{ marginBottom: 16 }}>
        <Col xs={24} sm={8} md={8} style={{ marginBottom: 8 }}>
          <div
            role="button"
            tabIndex={0}
            style={summaryTileStyle(true)}
            onClick={clearQuickFilters}
            onKeyDown={event => {
              if (event.key === 'Enter' || event.key === ' ') {
                clearQuickFilters();
              }
            }}
          >
            <div style={{ color: 'rgba(0,0,0,0.45)', fontSize: 12 }}>Hosts</div>
            <div style={{ fontSize: 20, fontWeight: 600 }}>{summaryData.totalHosts || 0}</div>
            {(summaryData.unlinkedHosts || 0) > 0 && (
              <div style={{ fontSize: 12, color: 'rgba(0,0,0,0.45)' }}>{summaryData.unlinkedHosts} unlinked</div>
            )}
          </div>
        </Col>
        <Col xs={24} sm={8} md={8} style={{ marginBottom: 8 }}>
          <div style={summaryTileStyle(false)}>
            <div style={{ color: 'rgba(0,0,0,0.45)', fontSize: 12 }}>
              Companies{' '}
              <Tooltip title={COMPANIES_TOOLTIP}>
                <Icon type="info-circle" />
              </Tooltip>
            </div>
            <div style={{ fontSize: 20, fontWeight: 600 }}>{summaryData.totalCompanies || 0}</div>
          </div>
        </Col>
        <Col xs={24} sm={8} md={8} style={{ marginBottom: 8 }}>
          <div
            role="button"
            tabIndex={0}
            style={summaryTileStyle(true)}
            onClick={() => setQuickFilter('cancelScheduled', !(query.filter && query.filter.cancelScheduled))}
            onKeyDown={event => {
              if (event.key === 'Enter' || event.key === ' ') {
                setQuickFilter('cancelScheduled', !(query.filter && query.filter.cancelScheduled));
              }
            }}
          >
            <div style={{ color: 'rgba(0,0,0,0.45)', fontSize: 12 }}>
              Cancel scheduled{' '}
              <Tooltip title="Hosts with at least one company set to cancel at period end">
                <Icon type="info-circle" />
              </Tooltip>
            </div>
            <div style={{ fontSize: 20, fontWeight: 600 }}>
              {summaryData.cancelScheduledHosts || 0}
              <span style={{ fontSize: 13, fontWeight: 400, color: 'rgba(0,0,0,0.45)', marginLeft: 6 }}>
                ({summaryData.cancelScheduledCompanies || 0} companies)
              </span>
            </div>
          </div>
        </Col>
      </Row>

      <div style={{ marginBottom: 16, display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 8 }}>
        <span style={{ marginRight: 8 }}>Enrolled date:</span>
        <DatePicker.RangePicker
          allowClear
          value={enrolledRange}
          onChange={handleEnrolledRangeChange}
          format="DD/MM/YYYY"
          placeholder={['From', 'To']}
        />
        <span style={{ marginLeft: 8, marginRight: 4, color: 'rgba(0,0,0,0.45)' }}>Quick filters:</span>
        {QUICK_FILTER_CHIPS.map(chip => {
          const isActive = Boolean(query.filter && query.filter[chip.key]);
          return (
            <Tag
              key={chip.key}
              color={isActive ? 'blue' : undefined}
              style={{ cursor: 'pointer', marginRight: 0 }}
              onClick={() => setQuickFilter(chip.key, !isActive)}
            >
              {chip.label}
            </Tag>
          );
        })}
        {activeQuickFilters.length > 0 && (
          <Button type="link" size="small" onClick={clearQuickFilters} style={{ padding: 0 }}>
            Clear filters
          </Button>
        )}
      </div>

      <UsersTableWrap>
        <Table
          loading={isLoading}
          rowKey={record => String(record._id)}
          columns={constructColumns({ filters: tableQuery.filters || {}, sorter: tableQuery.sorter || {} })}
          dataSource={userList}
          onChange={handleTableChange}
          expandedRowRender={expandedCompanyRowRender}
          locale={{
            emptyText: activeQuickFilters.length ? 'No users match your filters' : 'No users found'
          }}
          pagination={{
            total: total || 0,
            current: query.currentPage || 1,
            pageSize: query.limit || 10,
            showSizeChanger: true,
            showTotal: (tableTotal, range) => `${range[0]}-${range[1]} of ${tableTotal}`
          }}
        />
      </UsersTableWrap>
    </StyledCard>
  );
};

export default withAppContext(withRouter(EInvoiceUsers));
